/** Collection navigation uses Shopify-rendered HTML, retaining market pricing and native cart forms. */
class PatchCollections extends HTMLElement {
  connectedCallback() {
    this.listeners?.abort();
    this.listeners = new AbortController();
    this.defaultKey = this.dataset.defaultKey || this.dataset.activeKey;
    this.hubPath = window.location.pathname;
    const { signal } = this.listeners;
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    window.addEventListener('popstate', () => this.restoreLocation(), { signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      const link = this.links.find((item) => item.dataset.blockId === event.detail.blockId);
      if (link) this.load(link, this.collectionURL(link), { history: false });
    }, { signal });
    this.rewritePagination();
    this.restoreLocation();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.request?.abort();
  }

  get links() { return [...this.querySelectorAll('[data-patch-collection]')]; }
  get results() { return this.querySelector('[data-patch-results]'); }

  findCollection(key) {
    const normalized = (key || '').trim().toLowerCase();
    return this.links.find((link) => link.dataset.key === normalized)
      || this.links.find((link) => link.dataset.handle === normalized);
  }

  collectionURL(link) {
    const url = new URL(window.location.href);
    url.pathname = this.hubPath;
    url.searchParams.set('collection', link.dataset.key);
    // A new collection always starts at page one; retain campaign and preview parameters.
    for (const key of [...url.searchParams.keys()]) {
      if (key === 'page' || key.startsWith('page_')) url.searchParams.delete(key);
    }
    url.hash = '';
    return url;
  }

  restoreLocation() {
    const url = new URL(window.location.href);
    const requested = url.searchParams.get('collection');
    const link = this.findCollection(requested) || this.findCollection(this.defaultKey);
    if (!link) return;
    // Invalid keys safely use the configured default, rather than loading arbitrary collections.
    const key = link.dataset.key;
    if (this.results.dataset.key !== key || this.renderedSearch !== url.search) {
      if (this.renderedSearch === undefined && this.results.dataset.key === key) {
        this.renderedSearch = url.search;
        this.updateSelection(key);
        return;
      }
      this.load(link, url, { history: false });
    }
  }

  onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('[data-patch-collection], [data-patch-page]');
    if (!link || !this.contains(link)) return;
    const isCollection = link.hasAttribute('data-patch-collection');
    const collection = isCollection ? link : this.findCollection(this.results.dataset.key);
    if (!collection) return;
    event.preventDefault();
    const url = isCollection ? this.collectionURL(collection) : new URL(link.href);
    this.load(collection, url, { history: true, focus: !isCollection });
  }

  async load(link, publicURL, options = {}) {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const key = link.dataset.key;
    const status = this.querySelector('[data-patch-status]');
    const error = this.querySelector('[data-patch-error]');
    error.hidden = true;
    status.textContent = 'Loading patches…';
    this.results.setAttribute('aria-busy', 'true');
    const renderURL = new URL(link.dataset.collectionUrl, window.location.origin);
    renderURL.search = publicURL.search;
    renderURL.searchParams.set('view', 'patch-collections');
    renderURL.searchParams.set('section_id', this.dataset.sectionId);
    const timer = setTimeout(() => request.abort(), 15000);
    try {
      const response = await fetch(renderURL, { signal: request.signal });
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-patch-results]');
      if (!next || next.dataset.handle !== link.dataset.handle) throw new Error('Unexpected collection response');
      if (request !== this.request) return;
      // Keep the navigation in place, so focus stays on the capsule the shopper used.
      this.results.replaceWith(next);
      // A configured alias can differ from the underlying collection handle.
      next.dataset.key = key;
      this.updateSelection(key);
      if (options.history && publicURL.href !== window.location.href) {
        window.history.pushState({}, '', publicURL);
      }
      this.renderedSearch = publicURL.search;
      this.rewritePagination();
      status.textContent = `${next.querySelector('[data-patch-heading]').textContent.trim()} loaded`;
      if (options.focus) next.querySelector('[data-patch-heading]').focus({ preventScroll: false });
    } catch (failure) {
      if (request !== this.request || !this.isConnected) return;
      status.textContent = '';
      error.hidden = false;
      // Native navigation remains available when fetching fails or times out.
      const fallback = new URL(renderURL);
      fallback.searchParams.delete('section_id');
      error.querySelector('[data-patch-fallback]').href = fallback.href;
    } finally {
      clearTimeout(timer);
      if (request === this.request) this.results.removeAttribute('aria-busy');
    }
  }

  updateSelection(key) {
    for (const link of this.links) {
      if (link.dataset.key === key) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    }
  }

  rewritePagination() {
    for (const link of this.querySelectorAll('[data-patch-page]')) {
      const rendered = new URL(link.href);
      const url = new URL(window.location.href);
      url.pathname = this.hubPath;
      url.searchParams.delete('section_id');
      for (const key of [...url.searchParams.keys()]) {
        if (key === 'page' || key.startsWith('page_')) url.searchParams.delete(key);
      }
      for (const [key, value] of rendered.searchParams) {
        if (key === 'page' || key.startsWith('page_')) url.searchParams.set(key, value);
      }
      url.searchParams.set('collection', this.results.dataset.key);
      link.href = url.href;
    }
  }
}

if (!customElements.get('tn-patch-collections')) customElements.define('tn-patch-collections', PatchCollections);
